import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { TrackRender } from 'app/shared/front-perfomance';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import { FeatureService } from 'app/state/feature.service';
import { TenantFeatures } from 'app/state/feature.repository';
import { PageTitleModel } from 'app/api/models/pageTitle/pageTitleModel';
import { SortState } from 'app/modules/shared/pipes/sort.pipe';
import { DocumentsRepository, IDocument } from 'app/state/document.repository';
import { DocumentsService } from 'app/state/document.service';
import {
  DocumentTypesRepository,
  IDocumentType,
} from 'app/state/documenttype.repository';
import { DocumentTypesService } from 'app/state/documenttype.service';

type DocumentsTab = 'documents' | 'types';

@UntilDestroy()
@TrackRender()
@Component({
  selector: 'app-admin-document-page',
  templateUrl: './admin-document-page.component.html',
  styleUrls: ['./admin-document-page.component.scss'],
  standalone: false,
})
export class AdminDocumentPageComponent implements OnInit {
  activeTab: DocumentsTab = 'documents';
  pageTitle: PageTitleModel = new PageTitleModel();

  searchDocuments = '';
  searchTypes = '';

  documentsSort: SortState = {
    parameter: { property: 'Title' },
    direction: 'asc',
  };
  typesSort: SortState = { parameter: { property: 'name' }, direction: 'asc' };

  deleteDocument: IDocument | null = null;
  deleteType: IDocumentType | null = null;

  constructor(
    public repo: DocumentsRepository,
    public service: DocumentsService,
    public typesRepo: DocumentTypesRepository,
    public typesService: DocumentTypesService,
    private generalData: GeneralDataRepository,
    private featureService: FeatureService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.generalData.allGeneralData$,
      this.generalData.customDescriptionsObservable$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([generalData, descriptions]) => {
        this.pageTitle = this.featureService.buildPageTitle(
          generalData.tenantMenuItemsData,
          descriptions,
          TenantFeatures.Documents,
        );
      });

    this.service
      .sort(this.documentsSort, this.searchDocuments)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        setTimeout(() => {
          console.log('APP_READY_DocumentListAdmin');
        }, 1000);
      });
    this.typesService
      .sortCustom(this.typesSort, this.searchTypes)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  changeTab(tab: DocumentsTab) {
    this.activeTab = tab;
  }

  addButtonLink() {
    return this.activeTab === 'documents'
      ? '/admin/documents/new'
      : '/admin/documenttype/new';
  }

  onSearch(value: string) {
    if (this.activeTab === 'documents') {
      this.searchDocuments = value;
      this.service.searchReloadPage(this.searchDocuments).subscribe();
    } else {
      this.searchTypes = value;
      this.typesService.docSearchRelodePage(this.searchTypes).subscribe();
    }
  }

  sortDocuments(property: string) {
    this.documentsSort = this.nextSort(this.documentsSort, property);
    this.service.sort(this.documentsSort, this.searchDocuments).subscribe();
  }

  sortTypes(property: string) {
    this.typesSort = this.nextSort(this.typesSort, property);
    this.typesService.sortCustom(this.typesSort, this.searchTypes).subscribe();
  }

  private nextSort(current: SortState, property: string): SortState {
    if (current.parameter.property === property) {
      return {
        parameter: current.parameter,
        direction: current.direction === 'asc' ? 'desc' : 'asc',
      };
    }
    return { parameter: { property }, direction: 'asc' };
  }

  isSortedBy(sort: SortState, property: string) {
    return sort.parameter.property === property;
  }

  sortArrow(sort: SortState, property: string) {
    return this.isSortedBy(sort, property) && sort.direction === 'asc'
      ? '▲'
      : '▼';
  }

  onDocumentsPageChange(page: number) {
    this.service.loadFilterPage(this.searchDocuments, page).subscribe();
  }

  onTypesPageChange(page: number) {
    this.typesService.loadPageCustom(page, undefined, this.searchTypes).subscribe();
  }

  editDocument(doc: IDocument) {
    this.router.navigate(['/admin/documents/edit/', doc.id]);
  }

  editType(type: IDocumentType) {
    this.router.navigate(['/admin/documents/edit/documenttype/', type.id]);
  }

  confirmDeleteDocument() {
    if (!this.deleteDocument) return;
    this.service.delete(this.deleteDocument.id).subscribe(() => {
      this.service.reloadPage(this.searchDocuments).subscribe();
    });
    this.deleteDocument = null;
  }

  confirmDeleteType() {
    if (!this.deleteType) return;
    this.typesService.deleteCustom(this.deleteType.id).subscribe(() => {
      this.typesService.reloadPageCustom(this.searchTypes).subscribe();
    });
    this.deleteType = null;
  }

  setShownOnSideMenu(type: IDocumentType, isShownOnSideMenu: boolean) {
    this.typesService
      .updateDocumenttype({
        id: type.id,
        name: type.name,
        isShownOnSideMenu,
      })
      .subscribe((updated) => {
        type.isShownOnSideMenu = updated
          ? isShownOnSideMenu
          : !isShownOnSideMenu;
        if (updated) {
          this.generalData.updateAllGeneralData(true);
        }
      });
  }
}
